/**
 * @element qds-form-grid
 *
 * Layout container for a set of fields. Implements its own CSS Grid (PrimeFlex
 * classes cannot cross a shadow boundary) and reads column spans from a
 * `data-span` attribute on its light-DOM children.
 *
 * @attr {1|2|3|4} columns - Maximum columns at the widest breakpoint. Default `1`.
 * @attr {sm|md|lg} gap - Row and column gap. Default `md`.
 * @attr {sm|md|lg} row-gap - Overrides the row gap only. Defaults to `gap`.
 * @attr {vertical|horizontal} label-orientation - Pushed to every slotted `qds-field`. Default `vertical`.
 * @attr {boolean} dense - Sets `size="sm"` on slotted fields and field-consumer controls.
 *
 * @csspart grid - The CSS Grid container.
 *
 * @example
 * <qds-form-grid columns="2" gap="md">
 *   <qds-field label="Account name"><qds-text-input></qds-text-input></qds-field>
 *   <qds-field label="Account code"><qds-text-input></qds-text-input></qds-field>
 *   <qds-field label="Description" data-span="2"><qds-text-input></qds-text-input></qds-field>
 * </qds-form-grid>
 */

import { loadBaseCSS } from '../shared/button-utils.js';

/** Gap token mapping: column-gap and row-gap for each `gap` value. */
interface GapPair {
  col: string;
  row: string;
}

const GAP_MAP: Record<string, GapPair> = {
  sm: {
    col: 'var(--theme-space-3, 12px)',
    row: 'var(--theme-space-3, 12px)',
  },
  md: {
    col: 'var(--theme-space-6, 24px)',
    row: 'var(--theme-space-4, 16px)',
  },
  lg: {
    col: 'var(--theme-space-10, 40px)',
    row: 'var(--theme-space-6, 24px)',
  },
};

const ROW_GAP_MAP: Record<string, string> = {
  sm: 'var(--theme-space-3, 12px)',
  md: 'var(--theme-space-4, 16px)',
  lg: 'var(--theme-space-6, 24px)',
};

export class QdsFormGrid extends HTMLElement {
  private _columns: number = 1;
  private _gap: string = 'md';
  private _rowGap: string | null = null;
  private _labelOrientation: string = 'vertical';
  private _dense: boolean = false;

  private _slotChangeHandler: (() => void) | null = null;

  static get observedAttributes(): string[] {
    return ['columns', 'gap', 'row-gap', 'label-orientation', 'dense'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;
    this._syncPrivateFields();
    this._applyCssProperties();
    this.render();

    if (!this._slotChangeHandler) {
      this._slotChangeHandler = (): void => this._applyChildAttributes();
    }
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot && this._slotChangeHandler) {
      slot.addEventListener('slotchange', this._slotChangeHandler);
    }

    this._applyChildAttributes();
  }

  disconnectedCallback(): void {
    if (this._slotChangeHandler) {
      const slot = this.shadowRoot?.querySelector('slot');
      if (slot) {
        slot.removeEventListener('slotchange', this._slotChangeHandler);
      }
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;
    this._syncPrivateFields();
    this._applyCssProperties();

    if (this.isConnected) {
      this._updateGridDiv();
      this._applyChildAttributes();
    }
  }

  // -- Private helpers --------------------------------------------------

  private _syncPrivateFields(): void {
    const cols = parseInt(this.getAttribute('columns') || '1', 10);
    this._columns = cols >= 1 && cols <= 4 ? cols : 1;

    const gap = this.getAttribute('gap');
    this._gap =
      gap === 'sm' || gap === 'md' || gap === 'lg' ? gap : 'md';

    const rowGap = this.getAttribute('row-gap');
    this._rowGap =
      rowGap === 'sm' || rowGap === 'md' || rowGap === 'lg' ? rowGap : null;

    const orientation = this.getAttribute('label-orientation');
    this._labelOrientation =
      orientation === 'vertical' || orientation === 'horizontal'
        ? orientation
        : 'vertical';

    this._dense = this.hasAttribute('dense');
  }

  private _applyCssProperties(): void {
    this.style.setProperty('--qds-form-grid-columns', String(this._columns));

    const gapInfo = GAP_MAP[this._gap] || GAP_MAP.md;
    this.style.setProperty('--qds-form-grid-column-gap', gapInfo.col);

    const rowGapValue = this._rowGap
      ? ROW_GAP_MAP[this._rowGap] || gapInfo.row
      : gapInfo.row;
    this.style.setProperty('--qds-form-grid-row-gap', rowGapValue);
  }

  /**
   * Returns the slotted elements assigned to the grid's slot, with a
   * fallback to `this.children` for environments where `assignedElements`
   * is unavailable or not yet populated (e.g. jsdom).
   */
  private _getAssignedElements(): Element[] {
    if (!this.shadowRoot) return [];
    const slot = this.shadowRoot.querySelector('slot') as HTMLSlotElement | null;
    if (!slot) return [];

    let assigned: Element[];
    if (typeof slot.assignedElements === 'function') {
      assigned = slot.assignedElements({ flatten: true });
    } else {
      assigned = [];
    }

    if (assigned.length === 0 && this.children.length > 0) {
      assigned = Array.from(this.children);
    }

    return assigned;
  }

  private _applySpan(el: Element): void {
    const htmlEl = el as HTMLElement;
    const spanAttr = el.getAttribute('data-span');

    if (!spanAttr || spanAttr === '') {
      htmlEl.style.removeProperty('grid-column');
      return;
    }

    if (spanAttr === 'full') {
      htmlEl.style.gridColumn = '1 / -1';
      return;
    }

    const span = parseInt(spanAttr, 10);
    if (isNaN(span) || span <= 0) {
      htmlEl.style.removeProperty('grid-column');
      return;
    }

    // Clamp to the columns attribute (max columns).
    // Container-query CSS handles further clamping at narrower widths.
    const clamped = Math.min(span, this._columns);
    htmlEl.style.gridColumn = `span ${clamped}`;
  }

  private _applyChildAttributes(): void {
    const assigned = this._getAssignedElements();

    for (const el of assigned) {
      // 1. Apply grid-column span based on data-span.
      this._applySpan(el);

      // 2. Push orientation to slotted qds-field elements.
      //    The grid always defines label-orientation (default vertical),
      //    so it always pushes orientation to qds-field children.
      if (el.tagName.toLowerCase() === 'qds-field') {
        el.setAttribute('orientation', this._labelOrientation);
      }

      // 3. When dense, push size="sm" to qds-field and field-consumer
      //    elements. Never clear what the grid does not define.
      if (this._dense) {
        if (el.tagName.toLowerCase() === 'qds-field') {
          el.setAttribute('size', 'sm');
        }

        const tagName = el.tagName.toLowerCase();
        // A field-consumer control slotted directly (not wrapped in
        // qds-field) may not have upgraded yet — its module can still be
        // loading when this runs. Checking its constructor too early
        // permanently misses the size push, since nothing else re-checks
        // once the real definition arrives (upgrade doesn't fire
        // slotchange). Defer via whenDefined instead of guessing wrong.
        const registered = tagName.includes('-') ? customElements.get(tagName) : undefined;
        if (tagName.includes('-') && (!registered || el.constructor !== registered)) {
          customElements.whenDefined(tagName).then(() => {
            if (this.isConnected) this._applyChildAttributes();
          });
        } else {
          const ctor = el.constructor as unknown as {
            qdsFieldConsumer?: boolean;
          };
          if (ctor.qdsFieldConsumer === true) {
            el.setAttribute('size', 'sm');
          }
        }
      }
    }
  }

  private _updateGridDiv(): void {
    if (!this.shadowRoot) return;
    const grid = this.shadowRoot.querySelector('.grid');
    if (grid) {
      grid.setAttribute('data-cols', String(this._columns));
    }
  }

  // -- Render ------------------------------------------------------------

  private render(): void {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          container-type: inline-size;
          /*
           * --qds-form-grid-columns, --qds-form-grid-column-gap, and
           * --qds-form-grid-row-gap are set on the host by JS from the
           * columns / gap / row-gap attributes.  Consumers may override
           * --qds-form-grid-min-column.
           */
          --qds-form-grid-min-column: 240px;
        }

        .grid {
          display: grid;
          grid-template-columns: repeat(var(--qds-form-grid-columns, 1), 1fr);
          column-gap: var(--qds-form-grid-column-gap);
          row-gap: var(--qds-form-grid-row-gap);
        }

        /*
         * Container-driven responsive behaviour.
         * No ResizeObserver, no JavaScript layout — pure CSS.
         *
         *   < 480px  -> 1 column
         *   480-767  -> min(2, columns)
         *   768-1023 -> min(3, columns)
         *   >= 1024  -> columns
         *
         * data-span values are clamped via ::slotted overrides so that
         * a span-2 field still spans the full width when the grid has
         * collapsed to one column.
         */
        @container (max-width: 479px) {
          .grid {
            grid-template-columns: repeat(1, 1fr);
          }
          ::slotted([data-span]) {
            grid-column: 1 / -1 !important;
          }
        }

        @container (min-width: 480px) and (max-width: 767px) {
          .grid[data-cols="1"] { grid-template-columns: repeat(1, 1fr); }
          .grid[data-cols="2"] { grid-template-columns: repeat(2, 1fr); }
          .grid[data-cols="3"] { grid-template-columns: repeat(2, 1fr); }
          .grid[data-cols="4"] { grid-template-columns: repeat(2, 1fr); }
          ::slotted([data-span="3"]) { grid-column: span 2 !important; }
          ::slotted([data-span="4"]) { grid-column: span 2 !important; }
        }

        @container (min-width: 768px) and (max-width: 1023px) {
          .grid[data-cols="1"] { grid-template-columns: repeat(1, 1fr); }
          .grid[data-cols="2"] { grid-template-columns: repeat(2, 1fr); }
          .grid[data-cols="3"] { grid-template-columns: repeat(3, 1fr); }
          .grid[data-cols="4"] { grid-template-columns: repeat(3, 1fr); }
          ::slotted([data-span="4"]) { grid-column: span 3 !important; }
        }

        @container (min-width: 1024px) {
          .grid[data-cols="1"] { grid-template-columns: repeat(1, 1fr); }
          .grid[data-cols="2"] { grid-template-columns: repeat(2, 1fr); }
          .grid[data-cols="3"] { grid-template-columns: repeat(3, 1fr); }
          .grid[data-cols="4"] { grid-template-columns: repeat(4, 1fr); }
        }
      </style>

      <div part="grid" class="grid" data-cols="${this._columns}">
        <slot></slot>
      </div>
    `;

    // Re-attach slotchange listener to the freshly created slot.
    if (this._slotChangeHandler) {
      const slot = this.shadowRoot.querySelector('slot');
      if (slot) {
        slot.addEventListener('slotchange', this._slotChangeHandler);
      }
    }
  }
}

if (!customElements.get('qds-form-grid')) {
  customElements.define('qds-form-grid', QdsFormGrid);
}
